import { useCallback, useState } from 'react';

import { PageTitle } from '@components/PageTitle';
import { Button } from '@components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@components/ui/command';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@components/ui/form';
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
} from '@components/ui/popover';
import { pages } from '@constants/pages';
import { useAuth } from '@contexts/auth';
import errorsValidation from '@errors/validation';
import { zodResolver } from '@hookform/resolvers/zod';
import { cn } from '@lib/ui/cn';
import { type DrugInteraction } from '@models/DrugInteraction';
import DrugService from '@services/drugs/DrugService';
import { type IGetAllDrugsOutputData } from '@services/drugs/dtos/getAllDrugs/OutputData';
import { type IVerifyInteractionsBetweenDrugsInputData } from '@services/drugs/dtos/verifyInteractionsBetweenDrugs/InputData';
import { type IVerifyInteractionsBetweenDrugsOutputData } from '@services/drugs/dtos/verifyInteractionsBetweenDrugs/OutputData';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import { useFieldArray, useForm } from 'react-hook-form';
import { Link } from 'react-router';
import { toast } from 'sonner';
import { z } from 'zod';

import Item from './Item';
import Loading from './Loading';

const formSchema = z.object({
  drugs: z.array(
    z.object({
      id: z.string().min(1, { message: errorsValidation.required }),
    }),
  ),
});

function DrugInteractions() {
  const [interactions, setInteractions] = useState<DrugInteraction[]>([]);
  const { user } = useAuth();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    mode: 'onBlur',
    defaultValues: {
      drugs: [{ id: '' }, { id: '' }],
    },
  });
  const { append, fields, remove } = useFieldArray({
    control: form.control,
    name: 'drugs',
  });

  const { isPending, mutateAsync } = useMutation<
    IVerifyInteractionsBetweenDrugsOutputData,
    unknown,
    IVerifyInteractionsBetweenDrugsInputData
  >({
    mutationFn: DrugService.verifyInteractions,
    onError: () => {
      toast.error('Atenção', {
        description:
          'Não foi possível verificar as interações. Tente novamente',
      });
    },
  });

  const { data: drugs } = useQuery<IGetAllDrugsOutputData>({
    queryFn: async () => {
      const response = await DrugService.getAll({
        userId: user?.id,
      });
      return response;
    },
    queryKey: ['drugs', user?.id],
    refetchOnWindowFocus: false,
  });

  function clearFields() {
    form
      .getValues('drugs')
      .forEach((_, index) => form.setValue(`drugs.${index}.id`, ''));
    setInteractions([]);
  }

  const handleSubmit = useCallback(
    async (values: z.infer<typeof formSchema>) => {
      const output = await mutateAsync({
        ids: values.drugs.map((item) => Number(item.id)),
      });
      setInteractions(output.list);
    },
    [mutateAsync],
  );

  return (
    <div>
      <PageTitle hasBackButton title="Interações medicamentosas" />
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)}>
          <div className="mb-8 grid grid-cols-1 items-end gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {fields.map((fields, index) => (
              <FormField
                control={form.control}
                key={fields.id}
                name={`drugs.${index}.id`}
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <div className="flex flex-row justify-between">
                      <FormLabel className="text-tertiary flex items-center gap-2">
                        {index + 1}º Medicamento{' '}
                        {index > 1 && (
                          <button
                            className="w-fit cursor-pointer"
                            onClick={() => remove(index)}
                            title="Remover"
                            type="button">
                            <X className="text-destructive size-5" />
                          </button>
                        )}
                      </FormLabel>
                      <FormMessage />
                    </div>
                    <Popover>
                      <PopoverTrigger asChild className="w-full">
                        <FormControl>
                          <Button
                            className={cn(
                              'aria-invalid:border-destructive hover:text-foreground text-foreground aria-[expanded=true]:border-secondary aria-invalid:focus-visible:ring-0 w-full justify-between font-normal hover:bg-transparent aria-[expanded=true]:outline-1 aria-[expanded=true]:ring-2',
                              !field.value && 'text-muted-foreground',
                            )}
                            role="combobox"
                            variant="outline">
                            {field.value
                              ? drugs?.list.find(
                                  (option) =>
                                    option.id.toString() === field.value,
                                )?.name
                              : ''}
                            <ChevronsUpDown className="ml-auto size-4 shrink-0 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="max-h-[var(--radix-popover-content-available-height)] w-[var(--radix-popover-trigger-width)] p-0">
                        <Command>
                          <CommandInput
                            className="placeholder:text-foreground/70 h-9"
                            placeholder="Buscar..."
                          />
                          <CommandList>
                            <CommandEmpty>
                              Nenhuma opção encontrada.
                            </CommandEmpty>
                            <CommandGroup>
                              {drugs?.list.map((option) => (
                                <CommandItem
                                  className="data-[selected=true]:bg-tertiary"
                                  key={option.id}
                                  onSelect={() => {
                                    form.setValue(
                                      `drugs.${index}.id`,
                                      option.id.toString(),
                                      { shouldValidate: true },
                                    );
                                  }}
                                  value={option.name}>
                                  <PopoverClose className="flex w-full flex-row items-center">
                                    <Check
                                      className={cn(
                                        'mr-2 h-4 w-4',
                                        option.id.toString() === field.value
                                          ? 'opacity-100'
                                          : 'opacity-0',
                                      )}
                                    />
                                    {option.name}
                                  </PopoverClose>
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </FormItem>
                )}
              />
            ))}
            <Button
              className="w-full max-w-56 flex-1 cursor-pointer"
              onClick={() => append({ id: '' })}
              title="Adicionar medicamento"
              type="button"
              variant="secondary">
              Adicionar medicamento
            </Button>
          </div>

          <div className="mt-8 grid grid-cols-1 gap-2 sm:grid-cols-[repeat(2,_15rem)] sm:justify-center">
            <Button
              className="cursor-pointer"
              onClick={clearFields}
              type="button"
              variant="destructive">
              Limpar
            </Button>
            {user?.accessGranted ? (
              <Button
                className="cursor-pointer"
                type="submit"
                variant="secondary">
                Verificar
              </Button>
            ) : (
              <Button
                asChild
                className="cursor-pointer"
                type="button"
                variant="secondary">
                <Link to={pages.payment}>Verificar</Link>
              </Button>
            )}
          </div>

          {isPending ? (
            [1, 2].map((item) => <Loading key={item} />)
          ) : (
            <div>
              {interactions.map((data, index) => (
                <Item data={data} key={index} />
              ))}
            </div>
          )}
        </form>
      </Form>
    </div>
  );
}

export default DrugInteractions;
